import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Plus, Trash2, Bell, BellOff, Clock, Mail, RotateCcw, 
  MapPin, Hash, Eye, FileText, AlertTriangle, CheckCircle,
  ChevronRight, Zap, Settings2, Smartphone, MessageSquare,
  Building2, ListChecks, Activity, Filter, MoreVertical,
  Pause, Play, Edit, Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useTierAccess } from '../hooks/useTierAccess';
import { UpgradePrompt } from '../components/UpgradePrompt';

// Monitor types
const MONITOR_TYPES = {
  address: { label: 'Address', icon: MapPin, color: 'indigo' },
  application: { label: 'Application #', icon: Hash, color: 'emerald' },
  watchlist: { label: 'Watchlist', icon: Eye, color: 'amber' }
};

// Trigger events - what to monitor for
const TRIGGER_EVENTS = [
  { id: 'new_violation', label: 'New Violation Filed', icon: AlertTriangle, description: 'When any new violation is filed against the property', category: 'violation' },
  { id: 'violation_dob', label: 'DOB Violation', icon: AlertTriangle, description: 'Department of Buildings violations only', category: 'violation' },
  { id: 'violation_ecb', label: 'ECB Violation', icon: AlertTriangle, description: 'Environmental Control Board violations', category: 'violation' },
  { id: 'violation_fire', label: 'Fire Violation', icon: AlertTriangle, description: 'Fire Department violations', category: 'violation' },
  { id: 'violation_high_severity', label: 'High Severity Violation', icon: AlertTriangle, description: 'Only Class 1 or Immediately Hazardous violations', category: 'violation' },
  { id: 'violation_status', label: 'Violation Status Change', icon: Activity, description: 'When a violation status changes (resolved, dismissed, etc.)', category: 'violation' },
  { id: 'new_application', label: 'New Application Filed', icon: FileText, description: 'When a new permit application is filed', category: 'application' },
  { id: 'application_status', label: 'Application Status Change', icon: CheckCircle, description: 'When application status changes (approved, denied, etc.)', category: 'application' },
  { id: 'inspection_scheduled', label: 'Inspection Scheduled', icon: Clock, description: 'When an inspection is scheduled for the property', category: 'inspection' },
  { id: 'ownership_change', label: 'Ownership Change', icon: Building2, description: 'When property ownership changes', category: 'other' },
];

// Group triggers by category
const TRIGGER_CATEGORIES = [
  { id: 'violation', label: 'Violation Alerts', color: 'rose' },
  { id: 'application', label: 'Application Alerts', color: 'indigo' },
  { id: 'inspection', label: 'Inspection Alerts', color: 'emerald' },
  { id: 'other', label: 'Other Alerts', color: 'zinc' },
];

// Action types - what to do when triggered
const ACTION_TYPES = [
  { id: 'email', label: 'Send Email', icon: Mail, description: 'Send email notification' },
  { id: 'sms', label: 'Send SMS', icon: Smartphone, description: 'Send text message (Pro+)' },
  { id: 'in_app', label: 'In-App Notification', icon: Bell, description: 'Show notification in dashboard' },
  { id: 'webhook', label: 'Webhook', icon: Zap, description: 'Send to external URL (Enterprise)' },
  { id: 'crm_update', label: 'Update CRM', icon: MessageSquare, description: 'Push update to connected CRM' },
];

export default function AlertsPage() {
  const { api, user } = useAuth();
  const { canAccess, isAdmin } = useTierAccess();
  const hasAccess = isAdmin || canAccess('email_alerts');
  const [monitors, setMonitors] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState(1); // Multi-step dialog
  
  // New monitor state
  const [newMonitor, setNewMonitor] = useState({
    name: '',
    type: 'address', // address, application, watchlist
    target: '', // address string, app number, or watchlist id
    targetLabel: '', // Display label
    triggers: [], // Array of trigger event IDs
    actions: ['in_app', 'email'], // Default actions
    frequency: 'instant', // instant, daily_digest, weekly_digest
    enabled: true,
    filters: {
      datasets: ['dob_violations'],
      severity: 'all'
    }
  });

  // Address autocomplete
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const fetchMonitors = async () => {
    try {
      const res = await api().get('/alerts');
      setMonitors(res.data || []);
    } catch (err) {
      console.error('Failed to fetch monitors:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlists = async () => {
    try {
      const res = await api().get('/watchlist');
      setWatchlists(res.data || []);
    } catch (err) {
      console.error('Failed to fetch watchlists:', err);
    }
  };

  useEffect(() => {
    fetchMonitors();
    fetchWatchlists();
  }, [api]);

  // Address lookup
  const fetchAddressSuggestions = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const res = await api().get(`/address/search?query=${encodeURIComponent(query)}`);
      setAddressSuggestions(res.data?.results || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Address lookup failed:', err);
    }
  }, [api]);

  const handleCreate = async () => {
    if (!newMonitor.name) {
      toast.error('Please enter a monitor name');
      return;
    }
    if (!newMonitor.target) {
      toast.error('Please select what to monitor');
      return;
    }
    if (newMonitor.triggers.length === 0) {
      toast.error('Please select at least one trigger event');
      return;
    }

    try {
      // Map to the existing alerts API structure
      await api().post('/alerts', {
        name: newMonitor.name,
        frequency: newMonitor.frequency === 'instant' ? 'daily' : newMonitor.frequency.replace('_digest', ''),
        email_enabled: newMonitor.actions.includes('email'),
        filters: {
          dataset: newMonitor.filters.datasets[0] || 'dob_violations',
          borough: 'all',
          severity: newMonitor.filters.severity,
          // Extended fields for new monitor system
          monitor_type: newMonitor.type,
          target: newMonitor.target,
          target_label: newMonitor.targetLabel,
          triggers: newMonitor.triggers,
          actions: newMonitor.actions
        }
      });
      
      toast.success('Monitor created successfully!');
      setDialogOpen(false);
      resetForm();
      fetchMonitors();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to create monitor');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this monitor?')) return;
    try {
      await api().delete(`/alerts/${id}`);
      toast.success('Monitor deleted');
      fetchMonitors();
    } catch (err) {
      toast.error('Failed to delete monitor');
    }
  };

  const handleToggle = async (id, enabled) => {
    try {
      // Toggle would update the alert - for now just show toast
      toast.success(enabled ? 'Monitor paused' : 'Monitor resumed');
    } catch (err) {
      toast.error('Failed to update monitor');
    }
  };

  const resetForm = () => {
    setNewMonitor({
      name: '',
      type: 'address',
      target: '',
      targetLabel: '',
      triggers: [],
      actions: ['in_app', 'email'],
      frequency: 'instant',
      enabled: true,
      filters: { datasets: ['dob_violations'], severity: 'all' }
    });
    setDialogStep(1);
  };

  const toggleTrigger = (triggerId) => {
    setNewMonitor(prev => ({
      ...prev,
      triggers: prev.triggers.includes(triggerId)
        ? prev.triggers.filter(t => t !== triggerId)
        : [...prev.triggers, triggerId]
    }));
  };

  const toggleAction = (actionId) => {
    setNewMonitor(prev => ({
      ...prev,
      actions: prev.actions.includes(actionId)
        ? prev.actions.filter(a => a !== actionId)
        : [...prev.actions, actionId]
    }));
  };

  // Get monitor type info
  const getMonitorType = (monitor) => {
    const type = monitor.filters?.monitor_type || 'address';
    return MONITOR_TYPES[type] || MONITOR_TYPES.address;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="alerts-page">
        {!hasAccess ? (
          <div>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <div className="p-2 bg-indigo-500/20 rounded-lg">
                  <Bell className="w-6 h-6 text-indigo-400" />
                </div>
                Monitoring & Alerts
              </h1>
              <p className="text-zinc-400 mt-1">Monitor addresses, applications, and watchlists for changes</p>
            </div>
            <div className="glass-card rounded-xl">
              <UpgradePrompt requiredTier="pro" feature="Email monitoring alerts" />
            </div>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Bell className="w-6 h-6 text-indigo-400" />
              </div>
              Monitoring & Alerts
            </h1>
            <p className="text-zinc-400 mt-1">Monitor addresses, applications, and watchlists for changes</p>
          </div>

          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }} 
            className="btn-primary"
            data-testid="create-monitor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Monitor
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="glass-card p-4 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <Activity className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{monitors.length}</p>
                <p className="text-xs text-zinc-500">Active Monitors</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <MapPin className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {monitors.filter(m => m.filters?.monitor_type === 'address' || !m.filters?.monitor_type).length}
                </p>
                <p className="text-xs text-zinc-500">Address Monitors</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Eye className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {monitors.filter(m => m.filters?.monitor_type === 'watchlist').length}
                </p>
                <p className="text-xs text-zinc-500">Watchlist Monitors</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-500/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">0</p>
                <p className="text-xs text-zinc-500">Alerts Today</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Setup Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              resetForm();
              setNewMonitor(prev => ({
                ...prev,
                name: 'Violation Alert',
                triggers: ['new_violation', 'violation_dob', 'violation_ecb', 'violation_fire', 'violation_high_severity']
              }));
              setDialogOpen(true);
            }}
            className="glass-card p-5 rounded-xl border border-rose-500/20 hover:border-rose-500/40 transition-all text-left group"
            data-testid="quick-violation-alert"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-rose-500/20 rounded-xl group-hover:bg-rose-500/30 transition-all">
                <AlertTriangle className="w-6 h-6 text-rose-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Violation Alert</h3>
                <p className="text-sm text-zinc-400">Get notified when any violation is filed - DOB, ECB, Fire, or high severity</p>
                <span className="inline-flex items-center gap-1 mt-2 text-xs text-rose-400">
                  <Plus className="w-3 h-3" />
                  Quick Setup
                </span>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              resetForm();
              setNewMonitor(prev => ({
                ...prev,
                name: 'Application Alert',
                triggers: ['new_application', 'application_status']
              }));
              setDialogOpen(true);
            }}
            className="glass-card p-5 rounded-xl border border-indigo-500/20 hover:border-indigo-500/40 transition-all text-left group"
            data-testid="quick-application-alert"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-indigo-500/20 rounded-xl group-hover:bg-indigo-500/30 transition-all">
                <FileText className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Application Alert</h3>
                <p className="text-sm text-zinc-400">Track permit applications and status changes</p>
                <span className="inline-flex items-center gap-1 mt-2 text-xs text-indigo-400">
                  <Plus className="w-3 h-3" />
                  Quick Setup
                </span>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              resetForm();
              setNewMonitor(prev => ({
                ...prev,
                name: 'Inspection Alert',
                triggers: ['inspection_scheduled']
              }));
              setDialogOpen(true);
            }}
            className="glass-card p-5 rounded-xl border border-emerald-500/20 hover:border-emerald-500/40 transition-all text-left group"
            data-testid="quick-inspection-alert"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-emerald-500/20 rounded-xl group-hover:bg-emerald-500/30 transition-all">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Inspection Alert</h3>
                <p className="text-sm text-zinc-400">Be notified when inspections are scheduled</p>
                <span className="inline-flex items-center gap-1 mt-2 text-xs text-emerald-400">
                  <Plus className="w-3 h-3" />
                  Quick Setup
                </span>
              </div>
            </div>
          </button>
        </div>

        {/* Monitor Types Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-4">
          <Button variant="ghost" className="bg-white/10 text-white">
            <ListChecks className="w-4 h-4 mr-2" />
            All Monitors
          </Button>
          <Button variant="ghost" className="text-zinc-400 hover:text-white">
            <MapPin className="w-4 h-4 mr-2" />
            Addresses
          </Button>
          <Button variant="ghost" className="text-zinc-400 hover:text-white">
            <Hash className="w-4 h-4 mr-2" />
            Applications
          </Button>
          <Button variant="ghost" className="text-zinc-400 hover:text-white">
            <Eye className="w-4 h-4 mr-2" />
            Watchlists
          </Button>
        </div>

        {/* Monitors List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        ) : monitors.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-indigo-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-indigo-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No Monitors Set Up</h3>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              Create monitors to track changes to addresses, applications, or entire watchlists. 
              Get notified when violations are filed, statuses change, or inspections are scheduled.
            </p>
            <Button onClick={() => setDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Monitor
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {monitors.map((monitor) => {
              const monitorType = getMonitorType(monitor);
              const MonitorIcon = monitorType.icon;
              const triggers = monitor.filters?.triggers || ['new_violation'];
              const actions = monitor.filters?.actions || ['email'];
              
              return (
                <div 
                  key={monitor.id} 
                  className="glass-card rounded-xl p-5 hover:border-white/20 transition-all"
                  data-testid={`monitor-card-${monitor.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Type Icon */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${monitorType.color}-500/20`}>
                        <MonitorIcon className={`w-6 h-6 text-${monitorType.color}-400`} />
                      </div>
                      
                      {/* Monitor Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-white">{monitor.name}</h3>
                          {monitor.email_enabled ? (
                            <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">Active</span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs bg-zinc-500/20 text-zinc-400 rounded-full">Paused</span>
                          )}
                        </div>
                        
                        {/* Target */}
                        <p className="text-sm text-zinc-400 mt-1 flex items-center gap-2">
                          <MonitorIcon className="w-4 h-4" />
                          {monitor.filters?.target_label || monitor.filters?.dataset?.replace(/_/g, ' ')}
                        </p>
                        
                        {/* Triggers & Actions */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {triggers.map(triggerId => {
                            const trigger = TRIGGER_EVENTS.find(t => t.id === triggerId);
                            if (!trigger) return null;
                            const TriggerIcon = trigger.icon;
                            return (
                              <span key={triggerId} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-indigo-500/10 text-indigo-300 rounded-lg border border-indigo-500/20">
                                <TriggerIcon className="w-3 h-3" />
                                {trigger.label}
                              </span>
                            );
                          })}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-3 mt-3 text-sm text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {monitor.frequency}
                          </span>
                          <span className="text-zinc-700">•</span>
                          {actions.map(actionId => {
                            const action = ACTION_TYPES.find(a => a.id === actionId);
                            if (!action) return null;
                            const ActionIcon = action.icon;
                            return (
                              <span key={actionId} className="flex items-center gap-1">
                                <ActionIcon className="w-4 h-4" />
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-white/10">
                        <DropdownMenuItem className="text-zinc-300 focus:bg-white/10 focus:text-white">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Monitor
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-zinc-300 focus:bg-white/10 focus:text-white">
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleToggle(monitor.id, monitor.email_enabled)}
                          className="text-zinc-300 focus:bg-white/10 focus:text-white"
                        >
                          {monitor.email_enabled ? (
                            <>
                              <Pause className="w-4 h-4 mr-2" />
                              Pause Monitor
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Resume Monitor
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(monitor.id)}
                          className="text-rose-400 focus:bg-rose-500/10 focus:text-rose-400"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create Monitor Dialog - Multi-step */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="glass-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-3">
                <div className="p-2 bg-indigo-500/20 rounded-lg">
                  <Plus className="w-5 h-5 text-indigo-400" />
                </div>
                Create New Monitor
              </DialogTitle>
            </DialogHeader>

            {/* Step Indicator */}
            <div className="flex items-center gap-2 py-4 border-b border-white/10">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    dialogStep >= step 
                      ? 'bg-indigo-500 text-white' 
                      : 'bg-white/10 text-zinc-500'
                  }`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`w-12 h-0.5 mx-2 ${
                      dialogStep > step ? 'bg-indigo-500' : 'bg-white/10'
                    }`} />
                  )}
                </div>
              ))}
              <div className="ml-4 text-sm text-zinc-400">
                {dialogStep === 1 && 'What to Monitor'}
                {dialogStep === 2 && 'Trigger Events'}
                {dialogStep === 3 && 'Actions & Settings'}
              </div>
            </div>

            {/* Step 1: What to Monitor */}
            {dialogStep === 1 && (
              <div className="space-y-6 py-4">
                <div>
                  <Label className="text-zinc-300 mb-3 block">Monitor Type</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(MONITOR_TYPES).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => setNewMonitor(prev => ({ ...prev, type: key, target: '', targetLabel: '' }))}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            newMonitor.type === key
                              ? `border-${config.color}-500 bg-${config.color}-500/10`
                              : 'border-white/10 bg-white/5 hover:bg-white/10'
                          }`}
                          data-testid={`monitor-type-${key}`}
                        >
                          <Icon className={`w-6 h-6 mb-2 text-${config.color}-400`} />
                          <p className="font-medium text-white">{config.label}</p>
                          <p className="text-xs text-zinc-500 mt-1">
                            {key === 'address' && 'Monitor a specific address'}
                            {key === 'application' && 'Monitor an application'}
                            {key === 'watchlist' && 'Monitor entire watchlist'}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Target Selection based on type */}
                {newMonitor.type === 'address' && (
                  <div className="relative">
                    <Label className="text-zinc-300 mb-2 block">Address to Monitor</Label>
                    <Input
                      placeholder="Start typing an address..."
                      value={newMonitor.targetLabel}
                      onChange={(e) => {
                        setNewMonitor(prev => ({ 
                          ...prev, 
                          target: e.target.value,
                          targetLabel: e.target.value 
                        }));
                        fetchAddressSuggestions(e.target.value);
                      }}
                      className="input-dark"
                      data-testid="address-input"
                    />
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-white/10 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                        {addressSuggestions.map((suggestion, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setNewMonitor(prev => ({
                                ...prev,
                                target: suggestion.address,
                                targetLabel: suggestion.label || suggestion.address
                              }));
                              setShowSuggestions(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-white/10 flex items-center gap-2"
                          >
                            <MapPin className="w-4 h-4 text-zinc-500" />
                            {suggestion.label || suggestion.address}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {newMonitor.type === 'application' && (
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Application / Job Filing Number</Label>
                    <Input
                      placeholder="Enter application number (e.g., 123456789)"
                      value={newMonitor.target}
                      onChange={(e) => setNewMonitor(prev => ({ 
                        ...prev, 
                        target: e.target.value,
                        targetLabel: `Application #${e.target.value}`
                      }))}
                      className="input-dark"
                      data-testid="application-input"
                    />
                  </div>
                )}

                {newMonitor.type === 'watchlist' && (
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Select Watchlist</Label>
                    <Select 
                      value={newMonitor.target}
                      onValueChange={(v) => {
                        const wl = watchlists.find(w => w.id === v);
                        setNewMonitor(prev => ({ 
                          ...prev, 
                          target: v,
                          targetLabel: wl?.name || 'Watchlist'
                        }));
                      }}
                    >
                      <SelectTrigger className="input-dark">
                        <SelectValue placeholder="Choose a watchlist" />
                      </SelectTrigger>
                      <SelectContent>
                        {watchlists.map((wl) => (
                          <SelectItem key={wl.id} value={wl.id}>
                            <div className="flex items-center gap-2">
                              <Eye className="w-4 h-4 text-amber-400" />
                              {wl.name} ({wl.properties?.length || 0} properties)
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-zinc-300 mb-2 block">Monitor Name</Label>
                  <Input
                    placeholder="Give this monitor a name..."
                    value={newMonitor.name}
                    onChange={(e) => setNewMonitor(prev => ({ ...prev, name: e.target.value }))}
                    className="input-dark"
                    data-testid="monitor-name-input"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Trigger Events */}
            {dialogStep === 2 && (
              <div className="space-y-6 py-4">
                <p className="text-sm text-zinc-400">Select what events should trigger this monitor:</p>
                
                {/* Grouped Triggers */}
                {TRIGGER_CATEGORIES.map((category) => {
                  const categoryTriggers = TRIGGER_EVENTS.filter(t => t.category === category.id);
                  if (categoryTriggers.length === 0) return null;
                  
                  return (
                    <div key={category.id} className="space-y-2">
                      <h4 className={`text-sm font-medium text-${category.color}-400 flex items-center gap-2`}>
                        <div className={`w-2 h-2 rounded-full bg-${category.color}-500`} />
                        {category.label}
                      </h4>
                      <div className="space-y-2 pl-4">
                        {categoryTriggers.map((trigger) => {
                          const TriggerIcon = trigger.icon;
                          const isSelected = newMonitor.triggers.includes(trigger.id);
                          return (
                            <button
                              key={trigger.id}
                              onClick={() => toggleTrigger(trigger.id)}
                              className={`w-full p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${
                                isSelected
                                  ? `border-${category.color}-500 bg-${category.color}-500/10`
                                  : 'border-white/10 bg-white/5 hover:bg-white/10'
                              }`}
                              data-testid={`trigger-${trigger.id}`}
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                isSelected ? `bg-${category.color}-500/30` : 'bg-white/10'
                              }`}>
                                <TriggerIcon className={`w-4 h-4 ${isSelected ? `text-${category.color}-400` : 'text-zinc-400'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                                  {trigger.label}
                                </p>
                                <p className="text-xs text-zinc-500 truncate">{trigger.description}</p>
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected ? `border-${category.color}-500 bg-${category.color}-500` : 'border-zinc-600'
                              }`}>
                                {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Quick Select */}
                <div className="pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-zinc-500">Quick select:</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setNewMonitor(prev => ({ 
                        ...prev, 
                        triggers: TRIGGER_EVENTS.filter(t => t.category === 'violation').map(t => t.id)
                      }))}
                      className="h-6 px-2 text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                    >
                      All Violations
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setNewMonitor(prev => ({ 
                        ...prev, 
                        triggers: TRIGGER_EVENTS.filter(t => t.category === 'application').map(t => t.id)
                      }))}
                      className="h-6 px-2 text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                    >
                      All Applications
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setNewMonitor(prev => ({ 
                        ...prev, 
                        triggers: TRIGGER_EVENTS.map(t => t.id)
                      }))}
                      className="h-6 px-2 text-xs text-white hover:bg-white/10"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setNewMonitor(prev => ({ ...prev, triggers: [] }))}
                      className="h-6 px-2 text-xs text-zinc-500 hover:text-zinc-300"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Actions & Settings */}
            {dialogStep === 3 && (
              <div className="space-y-6 py-4">
                <div>
                  <Label className="text-zinc-300 mb-3 block">When triggered, do the following:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {ACTION_TYPES.map((action) => {
                      const ActionIcon = action.icon;
                      const isSelected = newMonitor.actions.includes(action.id);
                      const isDisabled = (action.id === 'sms' && !['pro', 'enterprise'].includes(user?.tier)) ||
                                        (action.id === 'webhook' && user?.tier !== 'enterprise');
                      
                      return (
                        <button
                          key={action.id}
                          onClick={() => !isDisabled && toggleAction(action.id)}
                          disabled={isDisabled}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            isDisabled 
                              ? 'border-white/5 bg-white/5 opacity-50 cursor-not-allowed'
                              : isSelected
                                ? 'border-indigo-500 bg-indigo-500/10'
                                : 'border-white/10 bg-white/5 hover:bg-white/10'
                          }`}
                          data-testid={`action-${action.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <ActionIcon className={`w-5 h-5 ${isSelected ? 'text-indigo-400' : 'text-zinc-400'}`} />
                            <div className="flex-1">
                              <p className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                                {action.label}
                              </p>
                              <p className="text-xs text-zinc-500">{action.description}</p>
                              {action.id === 'webhook' && isSelected && !isDisabled && (
                                <a 
                                  href="/webhooks" 
                                  className="text-xs text-indigo-400 hover:text-indigo-300 mt-1 inline-block"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Configure webhooks →
                                </a>
                              )}
                            </div>
                            {isSelected && !isDisabled && (
                              <CheckCircle className="w-4 h-4 text-indigo-400" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-300 mb-2 block">Notification Frequency</Label>
                  <Select 
                    value={newMonitor.frequency}
                    onValueChange={(v) => setNewMonitor(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger className="input-dark">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instant">Instant (as it happens)</SelectItem>
                      <SelectItem value="daily_digest">Daily Digest</SelectItem>
                      <SelectItem value="weekly_digest">Weekly Digest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Summary */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h4 className="text-sm font-medium text-white mb-3">Monitor Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Name:</span>
                      <span className="text-white">{newMonitor.name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Monitoring:</span>
                      <span className="text-white">{newMonitor.targetLabel || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Triggers:</span>
                      <span className="text-white">{newMonitor.triggers.length} selected</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Actions:</span>
                      <span className="text-white">{newMonitor.actions.length} selected</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dialog Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <Button
                variant="ghost"
                onClick={resetForm}
                className="btn-ghost"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              
              <div className="flex gap-3">
                {dialogStep > 1 && (
                  <Button
                    variant="ghost"
                    onClick={() => setDialogStep(prev => prev - 1)}
                    className="btn-ghost"
                  >
                    Back
                  </Button>
                )}
                
                {dialogStep < 3 ? (
                  <Button
                    onClick={() => setDialogStep(prev => prev + 1)}
                    className="btn-primary"
                    disabled={
                      (dialogStep === 1 && (!newMonitor.name || !newMonitor.target)) ||
                      (dialogStep === 2 && newMonitor.triggers.length === 0)
                    }
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleCreate}
                    className="btn-primary"
                    data-testid="create-monitor-submit"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Monitor
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </>
        )}
      </div>
    </DashboardLayout>
  );
}
